import { useState, useEffect } from "react";
import { t, font } from "@realtyflows/ui";
import { getFolderActivity } from "../api";

/**
 * FolderTimeline — V2 Feature 2.5
 *
 * Horizontal visual timeline of key folder events. Distinct from the
 * vertical activity feed: this is a compressed, milestone-focused view
 * that fits in a single row.
 *
 * Events selected from the folder activity log by type — anchors:
 *   created → template_applied → envelope_sent → signed → status_changed (escrow) → status_changed (closed)
 *
 * Each anchor is a dot on a horizontal line with a date label.
 */

const KEY_TYPES = {
  folder_created:      { label: "Created",       color: "muted" },
  template_applied:    { label: "Template",      color: "amber" },
  envelope_sent:       { label: "Sent",          color: "sky" },
  envelope_signed:     { label: "Signed",        color: "emerald" },
  status_changed:      { label: "Status change", color: "sky" },
  close_summary_generated: { label: "Closed",    color: "emerald" },
};
const colorOf = (n) => ({ emerald: t.emerald, amber: t.amber, sky: t.sky, coral: t.coral, muted: t.textMuted })[n] || t.textMuted;

export default function FolderTimeline({ folderId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!folderId) return;
    getFolderActivity(folderId, { limit: 200 })
      .then((rows) => {
        const keys = rows.filter((r) => KEY_TYPES[r.type]);
        // status_changed → only keep escrow/closed transitions
        const filtered = keys.filter((r) => {
          if (r.type !== "status_changed") return true;
          const to = r.meta?.to;
          return to === "in_escrow" || to === "closed" || to === "active";
        });
        // sort ascending (oldest -> newest)
        filtered.reverse();
        setEvents(filtered);
      })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [folderId]);

  if (loading) return null;
  if (events.length < 2) {
    return (
      <div style={{ fontSize: 12, color: t.textMuted, fontStyle: "italic", padding: 10, fontFamily: font.body }}>
        Timeline will fill in as work happens in this folder.
      </div>
    );
  }

  const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

  // For status_changed events, use the `to` value as the label suffix.
  const labelFor = (e) => {
    if (e.type === "status_changed") {
      const to = e.meta?.to;
      if (to === "in_escrow") return "In Escrow";
      if (to === "closed") return "Closed";
      if (to === "active") return "Active";
      return KEY_TYPES.status_changed.label;
    }
    return KEY_TYPES[e.type]?.label || e.type;
  };
  const colorFor = (e) => {
    if (e.type === "status_changed" && e.meta?.to === "closed") return t.emerald;
    return colorOf(KEY_TYPES[e.type]?.color || "muted");
  };

  return (
    <div style={{ overflowX: "auto", padding: "10px 4px" }}>
      <div style={{ position: "relative", display: "flex", alignItems: "center", minWidth: 480 }}>
        {/* Connecting line */}
        <div style={{
          position: "absolute", left: 16, right: 16, top: "50%",
          height: 2, background: t.border, zIndex: 0,
        }} />

        {events.map((e, idx) => {
          const color = colorFor(e);
          return (
            <div key={e.id} style={{
              flex: 1, display: "flex", flexDirection: "column",
              alignItems: "center", gap: 4, position: "relative", zIndex: 1, minWidth: 80,
            }}>
              {/* Dot */}
              <div style={{
                width: 14, height: 14, borderRadius: 99,
                background: color, border: `2px solid ${t.surface}`,
                boxShadow: `0 0 0 2px ${color}30`,
              }} />
              <div style={{ fontSize: 10, fontWeight: 700, color: t.textPrimary, marginTop: 6, fontFamily: font.body, textAlign: "center", lineHeight: 1.2 }}>
                {labelFor(e)}
              </div>
              <div style={{ fontSize: 9, color: t.textMuted, fontFamily: font.mono }}>
                {fmtDate(e.created_at)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
